import { Directive, ElementRef, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { NEVER, fromEvent, merge, timer } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { OnDestroyBase } from '../../helpers/public-helpers';

const LONG_PRESS_DEFAULT_DELAY = 500;
const LONG_PRESS_DEFAULT_INTERVAL = 1000 / 8;

/**
 * Helper to mimic a "long press" event.
 *
 * Will wait for a configurable duration after `mousedown`/`touchstart` and trigger
 * in a configurable interval once the threshold is reached.
 *
 * Gets cancelled by `mouseup`, `touchend` and `touchcancel`.
 */
// inspired by true events: https://medium.com/@ankit.rana.1195/angular-long-press-directive-with-rxjs-c3d6a3eb8c60
@Directive({
    selector: '[oscLongPress]',
    standalone: false
})
export class LongPressDirective extends OnDestroyBase implements OnDestroy {
    /**
     * Override the default long press behaviour.
     *
     * Defaults:
     *   - initial delay / threshold: 500 ms
     *   - trigger interval (event fires every `x` ms after threshold): 1000 / 8 ms
     *
     * To customize the timings, provide
     * 1. one `number` value - this will overwrite the initial delay / threshold, keeping the default interval
     * 2. a `tuple` value - an array containing two numeric values:
     *    the former being the threshold, the latter being the interval
     */
    @Input('oscLongPress')
    public set config(value: number | [threshold: number | null, interval: number | null]) {
        if (!Array.isArray(value)) {
            this.triggerDelay = Math.max(value, 0) || LONG_PRESS_DEFAULT_DELAY;
        } else {
            this.triggerDelay = Math.max(value[0] ?? 0, 0) || LONG_PRESS_DEFAULT_DELAY;
            this.triggerInterval = Math.max(value[1] ?? 0, 0) || LONG_PRESS_DEFAULT_INTERVAL;
        }
    }

    /**
     * After the element has been pressed for the configured threshold duration (defaults to 500 ms),
     * this event will trigger in the configured interval (defaults to 1/8 of a second = 125 ms).
     *
     * e. g.: mousedown -> 500 ms pass -> long press event fired every 125 ms
     *
     * The event does not contain a value, it's simply a notification that the
     * element is still being pressed.
     */
    @Output('oscLongPress')
    public readonly longPress = new EventEmitter<never>();

    private triggerDelay = LONG_PRESS_DEFAULT_DELAY;
    private triggerInterval = LONG_PRESS_DEFAULT_INTERVAL;

    public constructor({ nativeElement: element }: ElementRef) {
        super();
        const mousedown = fromEvent<MouseEvent>(element, 'mousedown').pipe(
            filter((ev) => ev.button === 0),
            map(() => true)
        );
        const mouseup = fromEvent<MouseEvent>(element, 'mouseup').pipe(
            filter((ev) => ev.button === 0),
            map(() => false)
        );
        const touchstart = fromEvent<TouchEvent>(element, 'touchstart').pipe(map(() => true));
        const touchend = fromEvent<TouchEvent>(element, 'touchend').pipe(map(() => false));
        const touchcancel = fromEvent<TouchEvent>(element, 'touchcancel').pipe(map(() => false));

        // booleans emitting true will start the timer, false will cancel it
        merge(mousedown, mouseup, touchstart, touchend, touchcancel)
            .pipe(
                switchMap((start) => (start ? timer(this.triggerDelay, this.triggerInterval) : NEVER)),
                takeUntil(this.destroySubject)
            )
            .subscribe(() => this.longPress.emit());
    }
}
